import React, { useEffect, useRef, useState } from 'react';
import {
  Paper, Box, Grid2 as Grid, TextField, Collapse,
  IconButton, Typography, Divider, Chip, Tooltip,
} from '@mui/material';
import ExpandMoreIcon        from '@mui/icons-material/ExpandMore';
import ExpandLessIcon        from '@mui/icons-material/ExpandLess';
import DiscountIcon          from '@mui/icons-material/LocalOffer';
import AddCircleOutlineIcon  from '@mui/icons-material/AddCircleOutline';
import CommentIcon           from '@mui/icons-material/Comment';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { useSnackbar } from '../../components/SnackbarProvider';
import MyButton from '../MyButton';
import { ButtonType, PERMISSION_PRIX, PERMISSION_PRIX_REVIENT, PERMISSION_REMISE, TypeLigne } from '../../constants/enums';
import { usePermission } from '../../hooks/usePermission';
import {
  Article, GeneriqueData,
  Ligne_Document_Vente,
} from '../../models/models';
import QuantiteInput    from '../QuantiteInput';
import {
  createOrUpdateLigne, deleteLigne,
  setSelectedLigne, fetchLignesByDocumentId,
} from '../../reduxStore/LigneDocumentVenteSlice';
import ArticleAutoC   from '../AutoComplete/ArticleAutoC';
import { fetchDocumentVenteData, setComposantDepot } from '../../reduxStore/documentVenteSlice';
import SelectDepot    from '../Selects/SelectDepot';
import api            from '../../reduxStore/api';
import { formatNumberWithSpaces } from '../../utils/utils';

interface LigneDocumentVenteFormProps {
  onCancel: () => void;
  onSaved?: () => void;
}

// ── Couleurs par type_ligne ────────────────────────────────────────────────
const TYPE_COLORS: Record<TypeLigne, string> = {
  [TypeLigne.ARTICLE]:        'default',
  [TypeLigne.REMISE]:         'error',
  [TypeLigne.CHARGE]:         'warning',
  [TypeLigne.COMMENTAIRE]:    'info',
  [TypeLigne.COMPOSANT]:      'success',
  [TypeLigne.IMMOBILISATION]: 'secondary',
  [TypeLigne.TRANSFERT]:      'primary',
  [TypeLigne.AJUSTEMENT]:     'warning',
};

const TYPE_LABELS: Record<TypeLigne, string> = {
  [TypeLigne.ARTICLE]:        'Article',
  [TypeLigne.REMISE]:         'Remise',
  [TypeLigne.CHARGE]:         'Frais / Charge',
  [TypeLigne.COMMENTAIRE]:    'Commentaire',
  [TypeLigne.COMPOSANT]:      'Composant (auto)',
  [TypeLigne.IMMOBILISATION]: 'Immobilisation',
  [TypeLigne.TRANSFERT]:      'Transfert (auto)',
  [TypeLigne.AJUSTEMENT]:     'Ajustement (auto)',
};

// ── Valeur initiale selon type ────────────────────────────────────────────

function buildEmpty(
  type: TypeLigne,
  documentId: number,
  depotId: number,
  depot: GeneriqueData | null,
  composantDepot: GeneriqueData | null,
  isInventaire = false,
): Ligne_Document_Vente {
  return {
    document_vente_id: documentId,
    article_id:    type === TypeLigne.ARTICLE ? 0 : null,
    // Inventaire : quantité comptée saisie par l'opérateur — 0 par défaut pour éviter
    // qu'un « 1 » pré-rempli parte en régularisation à l'insu de l'opérateur.
    quantite:      isInventaire && type === TypeLigne.ARTICLE ? 0 : 1,
    prix_unitaire: 0,
    remise:        0,
    tva:           0,
    extra_note:    '',
    depot_id:      depotId,
    depot:         depot,
    // Dépôt composants par ligne — pré-rempli depuis le défaut fabrication du document.
    depot_composant_id: composantDepot?.id ?? null,
    depot_composant:    composantDepot ?? null,
    type_ligne:    type,
    libelle_libre: null,
    // Transients pour IMMOBILISATION (ignorés pour les autres types)
    duree_mois: type === TypeLigne.IMMOBILISATION ? 60 : undefined,
    methode:    type === TypeLigne.IMMOBILISATION ? 'LINEAIRE' : undefined,
  };
}

// ══════════════════════════════════════════════════════════════════════════
// Composant principal
// ══════════════════════════════════════════════════════════════════════════

const LigneDocumentVenteForm: React.FC<LigneDocumentVenteFormProps> = ({ onCancel, onSaved }) => {
  const { selectedLigne, ligneDOcData } = useSelector((state: any) => state.LigneDocumentVenteSlice);
  const { selectedDocument, filters, composantDepot } = useSelector((state: any) => state.DocumentVenteSlice);

  // Doc de fabrication OU de transfert : une ligne ARTICLE génère une contrepartie de
  // SORTIE de stock dans un second dépôt, choisi par ligne (pré-rempli depuis le défaut
  // du document, modifiable au besoin), obligatoire. Même champ réutilisé (depot_composant) :
  //   - fabrication → « Dépôt composant » (dépôt des composants consommés)
  //   - transfert   → « Dépôt source »    (dépôt d'où sort la marchandise)
  const isFabrication     = !!selectedDocument?.type_document?.est_fabrication;
  const isTransfert       = !!selectedDocument?.type_document?.est_transfert;
  // Inventaire : la ligne ARTICLE est un relevé de comptage. On saisit la quantité
  // COMPTÉE ; le backend calcule l'écart avec le stock théorique et génère la ligne
  // AJUSTEMENT (entrée/sortie). Pas de prix ni de remise à saisir.
  const isInventaire      = !!selectedDocument?.type_document?.est_inventaire;
  // BL / BRF : compte dans l'en-cours du plafond tiers — pas de remise ni de frais.
  const isEncours         = !!Number(selectedDocument?.type_document?.engage_encours ?? 0);
  // Sans le droit docVentes.remise : champ remise et bouton « Remise » masqués
  // (miroir de la garde backend LigneGuards.droitRemise).
  const peutRemise        = usePermission(PERMISSION_REMISE);
  // Sans le droit docVentes.prix : prix unitaire d'une ligne ARTICLE figé au prix catalogue
  // sur un document client / fournisseur hors fabrication (miroir de LigneGuards.droitPrix).
  const typeTiersDoc      = String(selectedDocument?.type_document?.type_tiers ?? '').toLowerCase();
  const prixFige          = !usePermission(PERMISSION_PRIX)
    && (typeTiersDoc === 'client' || typeTiersDoc === 'fournisseur') && !isFabrication;
  // Sans le droit de voir le coût (0156), sur un document valorisé au coût (is_prix_v = 0 :
  // achats, inventaire, bon d'entrée) : prix posé par l'API, champ prix absent
  // (miroir de LigneResolvers.prixAuCout).
  const voitCout          = usePermission(PERMISSION_PRIX_REVIENT);
  const prixAuCout        = !voitCout && !Number(selectedDocument?.type_document?.is_prix_v ?? 1);
  const needsSecondDepot  = isFabrication || isTransfert;
  const secondDepotLabel  = isTransfert ? 'Dépôt source' : 'Dépôt composant';
  // Sur un transfert, le dépôt principal de la ligne ARTICLE est l'ENTRÉE = destination.
  const mainDepotLabel    = isTransfert ? 'Dépôt destination' : isInventaire ? 'Dépôt inventorié' : 'Depot';
  const [expanded, setExpanded] = useState(true);

  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const [resetKey, setResetKey] = useState(0);
  const articleInputRef = useRef<HTMLInputElement | null>(null);

  // Verrou anti-double-soumission. `submittingRef` bloque la ré-entrée AVANT le
  // prochain render (un useState ne serait pas encore à jour au 2e appel synchrone) ;
  // `submitting` ne sert qu'au rendu (boutons désactivés + spinner).
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  // Type de formulaire actif (déterminé par la ligne sélectionnée ou le bouton cliqué)
  const [activeType, setActiveType] = useState<TypeLigne>(TypeLigne.ARTICLE);

  // Inventaire : stock théorique de l'article au dépôt sélectionné (lecture seule).
  // null = pas encore chargé / non applicable. L'écart affiché = quantité comptée − théorique.
  const [stockTheorique, setStockTheorique] = useState<number | null>(null);

  const defaultDocId  = selectedDocument?.id ?? -1;
  const defaultDepotId = selectedDocument?.depot_id ?? 0;
  const defaultDepot   = selectedDocument?.depot ?? null;

  // Dépôt composant proposé par défaut : le choix en cours sur le document, sinon le
  // dépôt du document lui-même. Évite de le ressaisir à chaque ligne, et reflète le
  // repli appliqué côté serveur (LigneHooks.fabrication) — un doc de fabrication créé
  // par dispatch hérite du dépôt du document faute d'UI pour le choisir.
  // PAS de repli sur un transfert : le dépôt SOURCE doit différer de la destination,
  // le pré-remplir avec le dépôt du document produirait un transfert sur place.
  const defaultComposantDepot = composantDepot ?? (isFabrication ? defaultDepot : null);

  const [formData, setFormData] = useState<Ligne_Document_Vente>(
    buildEmpty(TypeLigne.ARTICLE, defaultDocId, defaultDepotId, defaultDepot, defaultComposantDepot, isInventaire)
  );

  // Synchroniser quand une ligne existante est sélectionnée pour édition
  useEffect(() => {
    if (selectedLigne && selectedLigne.document_vente_id === selectedDocument?.id) {
      // Édition : le dépôt composants n'est pas porté par la ligne ARTICLE — on le
      // retrouve depuis le dépôt réel des lignes COMPOSANT générées par cette ligne.
      const compo = (ligneDOcData as Ligne_Document_Vente[])
        .find(l => l.composant_de_ligne_id === selectedLigne.id && !!l.depot);
      const depotComposant = compo?.depot ?? defaultComposantDepot ?? null;
      setFormData({ ...selectedLigne, depot_composant: depotComposant, depot_composant_id: depotComposant?.id ?? null });
      setActiveType(selectedLigne.type_ligne ?? TypeLigne.ARTICLE);
    } else {
      setFormData(buildEmpty(activeType, defaultDocId, defaultDepotId, defaultDepot, defaultComposantDepot, isInventaire));
    }
  }, [selectedLigne, selectedDocument, defaultComposantDepot]);

  // Fabrication/transfert : initialise le défaut (second dépôt) depuis une ligne de
  // contrepartie déjà en base (COMPOSANT ou TRANSFERT) — cas édition d'un doc existant.
  // Reset à null fait au changement de document (setSelectedDocument), donc on ne
  // réécrase pas un choix utilisateur en cours.
  useEffect(() => {
    if (needsSecondDepot && !composantDepot) {
      const compo = (ligneDOcData as Ligne_Document_Vente[])
        .find(l => (l.type_ligne === TypeLigne.COMPOSANT || l.type_ligne === TypeLigne.TRANSFERT) && !!l.depot);
      if (compo?.depot) dispatch(setComposantDepot(compo.depot));
    }
  }, [needsSecondDepot, composantDepot, ligneDOcData, dispatch]);

  // Inventaire : charge le stock théorique dès qu'un article + un dépôt sont choisis.
  // `filter_dept_id` (≠ injection axios `departement_id`) → paramètre p_departement_id de sp_select_stock.
  useEffect(() => {
    if (!isInventaire || activeType !== TypeLigne.ARTICLE || !formData.article_id || !formData.depot_id) {
      setStockTheorique(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/stock/getByFilters', {
          params: {
            article_id:    formData.article_id,
            depot_id:      formData.depot_id,
            filter_dept_id: selectedDocument?.departement_id ?? undefined,
            limit: 1, offset: 0,
          },
        });
        if (!cancelled) setStockTheorique(Number(res.data?.data?.[0]?.stock_reel ?? 0));
      } catch {
        if (!cancelled) setStockTheorique(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isInventaire, activeType, formData.article_id, formData.depot_id, selectedDocument?.departement_id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'extra_note' || name === 'libelle_libre' ? value : Number(value),
    }));
  };

  const handleDepotChange = (depot: GeneriqueData | null) =>
    setFormData(prev => ({ ...prev, depot_id: depot?.id ?? 0, depot }));

  // Dépôt composants par ligne. Le changement est « sticky » : il met aussi à jour le
  // défaut fabrication du document, pour que les lignes suivantes se pré-remplissent avec.
  const handleComposantDepotChange = (depot: GeneriqueData | null) => {
    setFormData(prev => ({ ...prev, depot_composant_id: depot?.id ?? null, depot_composant: depot }));
    dispatch(setComposantDepot(depot));
  };

  const resetForm = (type: TypeLigne = TypeLigne.ARTICLE) => {
    setActiveType(type);
    setFormData(buildEmpty(type, defaultDocId, defaultDepotId, defaultDepot, defaultComposantDepot, isInventaire));
    setResetKey(k => k + 1);
    dispatch(setSelectedLigne(null));
  };

  const refreshLignes = () => {
    dispatch(fetchLignesByDocumentId({ limit: 100, offset: 0, document_vente_id: defaultDocId }));
    dispatch(fetchDocumentVenteData(filters));
  };

  // Remise max autorisée de l'article sélectionné (plafond). max = 0 → aucune remise.
  const remiseMax      = Number(formData.article?.remise ?? 0);
  const remiseExceeded = activeType === TypeLigne.ARTICLE
    && !!formData.article_id
    && Number(formData.remise ?? 0) > remiseMax;

  const handleSubmit = async () => {
    // ── Verrou anti-double-soumission ──────────────────────────────────────────
    // handleSubmit part de DEUX sources (bouton "Ajouter" + <button submit> caché,
    // déclenché par Enter sur n'importe quel champ) et n'était protégé par rien : un
    // double-clic ou deux Enter avant la fin de la requête créaient 2-3 lignes
    // identiques. Depuis la suppression de l'index unique uq_doc_article (migration
    // 0101), plus aucun garde-fou côté DB — un même article peut légitimement
    // apparaître plusieurs fois, mais pas via un envoi involontairement répété.
    if (submittingRef.current) return;

    // Garde article : une ligne ARTICLE doit porter un article. `article_id` vaut 0 tant
    // qu'aucun article n'est sélectionné (cf. buildEmpty). Sans cette garde, le POST part
    // avec article_id=0 et c'est la FK qui rejette, avec un message incompréhensible pour
    // l'opérateur (« Référence invalide sur article_id »). Le cas se produit facilement :
    // le formulaire redonne le focus au champ article après chaque ajout et le bouton
    // submit caché soumet sur Enter, donc un Enter tapé avant que la sélection d'article
    // ne soit validée envoie la ligne à vide.
    if (activeType === TypeLigne.ARTICLE && !formData.article_id) {
      showSnackbar('Veuillez sélectionner un article.', 'warning');
      return;
    }
    // Garde fabrication/transfert : le second dépôt (composant ou source) est obligatoire
    // pour une ligne ARTICLE de ce type de doc (miroir de la garde backend dans _save).
    if (needsSecondDepot && activeType === TypeLigne.ARTICLE && !formData.depot_composant?.id) {
      showSnackbar(`Veuillez sélectionner le ${secondDepotLabel.toLowerCase()} pour cette ligne.`, 'warning');
      return;
    }
    // Garde remise : la remise appliquée ne peut dépasser la remise max de l'article
    // (miroir de la garde backend dans _save).
    if (remiseExceeded) {
      showSnackbar(`Remise ${formData.remise}% supérieure au maximum autorisé (${remiseMax}%) pour cet article.`, 'warning');
      return;
    }
    // Le dépôt composants (transient, par ligne) est déjà dans formData — le backend
    // l'applique à chaque ligne COMPOSANT générée par cette ligne ARTICLE.
    submittingRef.current = true;
    setSubmitting(true);
    const payload: Ligne_Document_Vente = formData;
    try {
      const result = await dispatch(createOrUpdateLigne(payload)).unwrap();
      // Warnings stock (négatif autorisé / sous seuil) priment sur le message succès
      // car SnackbarProvider ne garde qu'un message visible à la fois.
      if (result?.warnings?.length) {
        result.warnings.forEach((w) => showSnackbar(w, 'warning'));
      } else {
        showSnackbar('Ligne enregistrée avec succès !', 'success');
      }
      refreshLignes();
      resetForm(activeType);
      onSaved?.();
      // Retour du focus sur la sélection d'article après ajout
      setTimeout(() => articleInputRef.current?.focus(), 50);
    } catch (error) {
      showSnackbar((error as string) || 'Erreur lors de la sauvegarde.', 'error');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (submittingRef.current) return;
    if (selectedLigne && window.confirm('Voulez-vous vraiment supprimer cette ligne ?')) {
      submittingRef.current = true;
      setSubmitting(true);
      try {
        await dispatch(deleteLigne(selectedLigne.id!)).unwrap();
        showSnackbar('Ligne supprimée avec succès.', 'success');
        refreshLignes();
        resetForm();
      } catch (error) {
        showSnackbar((error as string) || 'Erreur lors de la suppression.', 'error');
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  const handleArticleChange = (article: Article | null) => {
    if (!article) {
      setFormData(prev => ({ ...prev, article_id: 0, article: undefined, prix_unitaire: 0, tva: 0, remise: 0 }));
      return;
    }
    // Le SP `sp_select_articleByFilters` mode `article` applique déjà l'IFNULL
    // override → défaut côté serveur (cf. migration 0026). Donc `article.prix_*`
    // sont déjà les valeurs effectives pour le dept courant.
    // Plus besoin de fetch séparé sur article_prix_departement.
    const usePrixV = selectedDocument?.type_document?.is_prix_v ?? true;
    const prixDefaut = usePrixV ? (article.prix_v ?? 0) : (article.prix_r ?? 0);
    setFormData(prev => ({
      ...prev,
      article_id:    article.id ?? 0,
      article,
      prix_unitaire: prixDefaut,
      // La TVA de la ligne provient de l'article (pas de champ de saisie TVA pour les
      // lignes ARTICLE) : sans cela, tva reste à 0 et total_ht = total_ttc (TVA non extraite).
      // Inventaire : valorisation du stock compté AU COÛT HT → pas de TVA sur la ligne.
      tva:           isInventaire ? 0 : (article.tva ?? 0),
      // `article.remise` est la remise MAXIMUM autorisée, pas la remise à appliquer :
      // la remise de la ligne ne s'auto-remplit pas, elle reste à 0 (saisie manuelle).
      remise:        0,
    }));
  };

  // ── Boutons de sélection du type ────────────────────────────────────────

  const typeButtons: { type: TypeLigne; label: string; icon: React.ReactNode; color: string }[] = [
    { type: TypeLigne.ARTICLE,        label: 'Article',        icon: <AddCircleOutlineIcon fontSize="small" />, color: '#1976d2' },
    { type: TypeLigne.REMISE,         label: 'Remise',         icon: <DiscountIcon fontSize="small" />,         color: '#d32f2f' },
    { type: TypeLigne.CHARGE,         label: 'Frais',          icon: <AddCircleOutlineIcon fontSize="small" />, color: '#ed6c02' },
    { type: TypeLigne.COMMENTAIRE,    label: 'Commentaire',    icon: <CommentIcon fontSize="small" />,          color: '#0288d1' },
    { type: TypeLigne.IMMOBILISATION, label: 'Immobilisation', icon: <AddCircleOutlineIcon fontSize="small" />, color: '#6d28d9' },
  ];

  // IMMOBILISATION : uniquement sur facture fournisseur (acquisition).
  // Restrictions cumulées (miroir de la garde backend dans LigneDocumentVenteService._save) :
  //   - affecte_compte_fournisseur = 1  (doc fournisseur, pas client)
  //   - sens = 'D'                       (facture, pas avoir)
  // Sur un avoir fournisseur, le hook upsert créerait un bien fantôme — pour
  // annuler/céder un bien, l'utilisateur passe par la page Immobilisations.
  const visibleTypeButtons = typeButtons.filter(btn => {
    // Inventaire : seuls le comptage d'articles et les commentaires ont un sens
    // (miroir de la garde backend LigneGuards.inventaireTypeLigne).
    if (isInventaire) return btn.type === TypeLigne.ARTICLE || btn.type === TypeLigne.COMMENTAIRE;
    // BL / BRF (engage_encours) : remises et frais se saisissent sur la facture
    // (miroir de la garde backend LigneGuards.remiseChargeSurEncours). Une ligne déjà
    // REMISE / CHARGE en cours d'édition garde son bouton (saisie antérieure à la règle).
    if (isEncours && (btn.type === TypeLigne.REMISE || btn.type === TypeLigne.CHARGE)) {
      return selectedLigne?.type_ligne === btn.type;
    }
    // Sans droit de remise : pas de nouvelle ligne REMISE (une ligne REMISE existante
    // en cours d'édition garde son bouton — le backend refuse un montant modifié).
    if (!peutRemise && btn.type === TypeLigne.REMISE) {
      return selectedLigne?.type_ligne === TypeLigne.REMISE;
    }
    return btn.type !== TypeLigne.IMMOBILISATION
      || (!!selectedDocument?.type_document?.affecte_compte_fournisseur
          && selectedDocument?.type_document?.sens === 'D');
  });

  // ── Rendu du formulaire selon type ──────────────────────────────────────

  const renderForm = () => {
    if (activeType === TypeLigne.ARTICLE) {
      return (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <ArticleAutoC
              key={`article${resetKey}`}
              onArticleChange={handleArticleChange}
              selectedArticle={formData.article}
              externalRef={articleInputRef}
              departement_id={selectedDocument?.departement_id ?? null}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SelectDepot
              key={`depot${resetKey}`}
              label={mainDepotLabel}
              selectedDepot={formData.depot}
              onDepotChange={handleDepotChange}
              departement_id={selectedDocument?.departement_id ?? null}
            />
          </Grid>

          {needsSecondDepot && (
            <Grid size={{ xs: 12, sm: 4 }}>
              <SelectDepot
                key={`depot-composant${resetKey}`}
                label={secondDepotLabel}
                selectedDepot={formData.depot_composant}
                onDepotChange={handleComposantDepotChange}
                departement_id={selectedDocument?.departement_id ?? null}
                error={!formData.depot_composant?.id}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 4 }}>
            <QuantiteInput
              key={`qty-${selectedLigne?.id ?? resetKey}`}
              label={isInventaire ? 'Quantité comptée' : undefined}
              article={formData.article}
              value={formData.quantite}
              onChange={v => setFormData(prev => ({ ...prev, quantite: v }))}
            />
          </Grid>

          {isInventaire ? (
            <>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField
                  label="Stock théorique" size="small" fullWidth
                  value={stockTheorique ?? '—'}
                  InputProps={{ readOnly: true }}
                  helperText={!formData.article_id || !formData.depot_id ? 'Choisir article + dépôt' : undefined}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                {(() => {
                  const ecart = stockTheorique === null ? null : Number(formData.quantite ?? 0) - stockTheorique;
                  const color = ecart === null || ecart === 0 ? 'text.secondary' : ecart > 0 ? 'success.main' : 'error.main';
                  // Valorisation informative au coût de l'article (comme le coût de fabrication),
                  // absente sans le droit de voir le coût.
                  const cout = Number(formData.article?.prix_r ?? 0);
                  const valeur = ecart === null ? null : ecart * cout;
                  const help = ecart === null
                    ? undefined
                    : ecart === 0
                      ? 'Conforme'
                      : `${ecart > 0 ? 'Entrée' : 'Sortie'}`
                        + (voitCout ? ` · ${valeur! > 0 ? '+' : ''}${formatNumberWithSpaces(valeur)} au coût` : '');
                  return (
                    <TextField
                      label="Écart (comptée − théorique)" size="small" fullWidth
                      value={ecart === null ? '—' : (ecart > 0 ? `+${ecart}` : String(ecart))}
                      InputProps={{ readOnly: true, sx: { color, fontWeight: 600 } }}
                      helperText={help}
                      FormHelperTextProps={{ sx: { color } }}
                    />
                  );
                })()}
              </Grid>
            </>
          ) : (
            <>
              {!prixAuCout && (
                <Grid size={{ xs: 6, sm: 4 }}>
                  <TextField
                    label="Prix Unitaire HT" name="prix_unitaire" type="number"
                    value={formData.prix_unitaire} onChange={handleChange}
                    size="small" fullWidth
                    disabled={prixFige}
                    helperText={prixFige ? 'Prix catalogue' : undefined}
                    // step 'any' : sans lui, le navigateur aligne le pas (1) sur le prix pré-rempli
                    // (ex. 274,73) et refuse à la validation toute saisie ronde comme 275.
                    inputProps={{ step: 'any' }}
                  />
                </Grid>
              )}
              {peutRemise && (
                <Grid size={{ xs: 6, sm: 4 }}>
                  <TextField
                    label="Remise (%)" name="remise" type="number"
                    value={formData.remise ?? ''} onChange={handleChange}
                    size="small" fullWidth
                    inputProps={{ step: 'any' }}
                    error={remiseExceeded}
                    helperText={remiseExceeded ? `Max autorisé : ${remiseMax}%` : undefined}
                  />
                </Grid>
              )}
            </>
          )}

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Note" name="extra_note"
              value={formData.extra_note ?? ''} onChange={handleChange}
              size="small" fullWidth
            />
          </Grid>
        </Grid>
      );
    }

    if (activeType === TypeLigne.REMISE) {
      return (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Libellé de la remise" name="libelle_libre"
              value={formData.libelle_libre ?? ''} onChange={handleChange}
              size="small" fullWidth
              placeholder="ex : Remise commerciale, Escompte de règlement…"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Tooltip title="Montant négatif = soustraction du total">
              <TextField
                label="Montant (négatif)" name="prix_unitaire" type="number"
                value={formData.prix_unitaire} onChange={handleChange}
                size="small" fullWidth
                inputProps={{ step: 'any' }}
              />
            </Tooltip>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="TVA (%)" name="tva" type="number"
              value={formData.tva ?? 0} onChange={handleChange}
              size="small" fullWidth
              inputProps={{ step: 'any' }}
            />
          </Grid>
        </Grid>
      );
    }

    if (activeType === TypeLigne.CHARGE) {
      return (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Libellé des frais" name="libelle_libre"
              value={formData.libelle_libre ?? ''} onChange={handleChange}
              size="small" fullWidth
              placeholder="ex : Frais de port, Emballage, Assurance…"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Montant HT" name="prix_unitaire" type="number"
              value={formData.prix_unitaire} onChange={handleChange}
              size="small" fullWidth
              inputProps={{ step: 'any', min: '0' }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="TVA (%)" name="tva" type="number"
              value={formData.tva ?? 0} onChange={handleChange}
              size="small" fullWidth
              inputProps={{ step: 'any' }}
            />
          </Grid>
        </Grid>
      );
    }

    if (activeType === TypeLigne.IMMOBILISATION) {
      return (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Libellé de l'immobilisation" name="libelle_libre"
              value={formData.libelle_libre ?? ''} onChange={handleChange}
              size="small" fullWidth
              placeholder="ex : Ordinateur portable, Mobilier de bureau, Véhicule…"
              required
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              label="Montant HT" name="prix_unitaire" type="number"
              value={formData.prix_unitaire} onChange={handleChange}
              size="small" fullWidth
              inputProps={{ step: 'any', min: '0' }}
              required
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              label="TVA (%)" name="tva" type="number"
              value={formData.tva ?? 0} onChange={handleChange}
              size="small" fullWidth
              inputProps={{ step: 'any' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Durée d'amortissement (mois)" name="duree_mois" type="number"
              value={formData.duree_mois ?? 60} onChange={handleChange}
              size="small" fullWidth
              inputProps={{ min: '1', step: '1' }}
              required
              helperText="60 = 5 ans · 120 = 10 ans"
            />
          </Grid>
        </Grid>
      );
    }

    if (activeType === TypeLigne.COMMENTAIRE) {
      return (
        <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Texte du commentaire" name="libelle_libre"
              value={formData.libelle_libre ?? ''} onChange={handleChange}
              size="small" fullWidth multiline rows={2}
              placeholder="Ce texte apparaîtra dans le document imprimé mais n'affecte pas les totaux."
            />
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  return (
    <Paper elevation={1} sx={{ padding: 1.5, marginBottom: 1.5 }}>

      {/* ── En-tête ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {selectedLigne ? 'Modifier la ligne' : 'Ajouter une ligne'}
          </Typography>
          {activeType !== TypeLigne.ARTICLE && (
            <Chip
              label={TYPE_LABELS[activeType]}
              size="small"
              color={TYPE_COLORS[activeType] as any}
              variant="outlined"
            />
          )}
        </Box>
        <IconButton size="small" onClick={() => setExpanded(p => !p)}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {/* ── Sélecteurs de type (masqués en édition) ── */}
        {!selectedLigne && (
          <Box display="flex" gap={1} mt={1} flexWrap="wrap">
            {visibleTypeButtons.map(btn => (
              <Box
                key={btn.type}
                onClick={() => resetForm(btn.type)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1.5, py: 0.5,
                  borderRadius: 2, cursor: 'pointer',
                  border: '1px solid',
                  borderColor: activeType === btn.type ? btn.color : 'divider',
                  bgcolor: activeType === btn.type ? `${btn.color}18` : 'transparent',
                  color:   activeType === btn.type ? btn.color : 'text.secondary',
                  fontSize: 13, fontWeight: activeType === btn.type ? 700 : 400,
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: btn.color, color: btn.color },
                }}
              >
                {btn.icon}
                {btn.label}
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <form onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
          {renderForm()}

          {/* Bouton submit caché : active la soumission via Enter sur n'importe quel champ.
              Désactivé pendant l'envoi → Enter ne peut plus empiler une 2e soumission. */}
          <button type="submit" style={{ display: 'none' }} aria-hidden="true" disabled={submitting} />

          <Box mt={1} display="flex" gap={1}>
            <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleSubmit} disabled={submitting} loading={submitting}>
              {selectedLigne ? 'Modifier' : 'Ajouter'}
            </MyButton>
            {selectedLigne && (
              <MyButton actionType={ButtonType.DELETE} variant="outlined" onClick={handleDelete} disabled={submitting}>
                Supprimer
              </MyButton>
            )}
            <MyButton actionType={ButtonType.CANCEL} variant="outlined" onClick={() => { resetForm(); onCancel(); }} disabled={submitting}>
              Annuler
            </MyButton>
          </Box>
        </form>
      </Collapse>
    </Paper>
  );
};

export default LigneDocumentVenteForm;
